/**
 * Mobile Navigation Hook
 * Clean, simplified version for handling navigation
 * 
 * Handles browser back button and navigation state
 * NOTE: State saving is handled by useMobileSessionStorage hook
 */

import { useEffect, useRef, RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export interface UseMobileNavigationProps {
  transactionType: "expense" | "income" | "transfer";
  workspaceSlug: string | undefined;
  routeStorageKey: string;
  isNavigatingBackRef: RefObject<boolean>;
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * Hook for handling navigation-related logic
 * 
 * Design principles:
 * - Single responsibility: Only handles navigation (browser back button)
 * - No state saving: That's handled by useMobileSessionStorage
 * - Simple: Just handles popstate events
 */
export function useMobileNavigation({
  transactionType,
  workspaceSlug,
  routeStorageKey,
  isNavigatingBackRef,
  location,
  navigate,
}: UseMobileNavigationProps) {
  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // When browser back button is pressed on transaction page
      if (location.pathname.includes('/create-transaction/') || location.pathname.includes('/edit-transaction/')) {
        // Mark that we're intentionally navigating back
        isNavigatingBackRef.current = true;
        
        // Clear route info to prevent Activity from redirecting back
        try {
          sessionStorage.removeItem(routeStorageKey);
        } catch (error) {
          console.error("Error clearing route info:", error);
        }
        
        // Navigate to activity page
        if (workspaceSlug) {
          navigate(`/${workspaceSlug}/activity`, { replace: true });
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [workspaceSlug, navigate, location.pathname, routeStorageKey, isNavigatingBackRef]);
}
